import React from 'react';
import { useLocation } from 'react-router-dom';
import { isEmail } from 'ts-utils';
import { recordError } from './Errors';

const USER_ID_PATTERN = /^[a-zA-Z0-9]+$/;

// Adapted from https://v5.reactrouter.com/web/example/query-parameters
export function useQuery(): URLSearchParams {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

// Used to dedupe userIdFromQueryParams errors below
let hasRecordedUserIdError = false;

/**
 * Get and validate the `userId` query param. Invalid params will return `undefined`.
 *
 * Due to an issue with some vero email links the userId param is sometimes set to '\{\{user.id\}\}'
 * (see PRO-5857 and #pro-5857-user-id-issue).
 * Because of this we want to disregard any `userId` param that is set to '\{\{user.id\}\}'.
 */
export function userIdFromQueryParams(query: URLSearchParams): string | undefined {
  let userIdParam = query.get('userId') ?? undefined;

  if (userIdParam !== undefined && !USER_ID_PATTERN.test(userIdParam)) {
    if (!hasRecordedUserIdError) {
      recordError(new Error(`userId query param is invalid: '${userIdParam}'`));
      hasRecordedUserIdError = true;
    }
    userIdParam = undefined;
  }

  return userIdParam;
}

/**
 * Detects any invalid or insecure parameters present in the URL used to open the app.
 * For now these are simply reported as errors to Sentry, allowing the source of the URLs to be found and the
 * invalid params removed.
 */
function reportInvalidParams(): void {
  const params = new URLSearchParams(window.location.search);

  if (params.has('email')) {
    recordEmailParamError("'email' parameter found");
  }

  const allValues = Array.from(params.values());
  if (allValues.some(isEmail)) {
    recordEmailParamError('Email address found');
  }
}

function recordEmailParamError(message: string): void {
  recordError(
    new Error(
      `${message} in ${window.location}. ` +
        'Personally identifiable information should not be included in query parameters'
    )
  );
}

function initialize(): void {
  reportInvalidParams();
}

export const QueryParams = {
  initialize,
};
